<template>
  <div class="profile-page">
    <AppHeader />

    <main class="profile-page__main">
      <div v-if="!isLoggedIn" class="profile-page__guest">
        <p>请先登录后查看个人中心</p>
        <router-link to="/" class="profile-page__guest-link">返回首页</router-link>
      </div>

      <div v-else class="profile-page__layout">
        <aside class="profile-page__sidebar">
          <div class="profile-page__user-card">
            <div class="profile-page__avatar">
              <i class="el-icon-user" />
            </div>
            <h1 class="profile-page__name">{{ userInfo.userName || '用户' }}</h1>
            <p class="profile-page__account">账号：{{ userInfo.userAccount || '-' }}</p>
            <p v-if="userInfo.userEmail" class="profile-page__email">邮箱：{{ userInfo.userEmail }}</p>
          </div>

          <nav class="profile-page__tabs">
            <button
              v-for="tab in historyTabs"
              :key="tab.value"
              type="button"
              class="profile-page__tab"
              :class="{ 'profile-page__tab--active': activeType === tab.value }"
              @click="switchTab(tab.value)"
            >
              {{ tab.label }}
            </button>
          </nav>

          <button type="button" class="profile-page__logout" @click="handleLogout">
            退出登录
          </button>
        </aside>

        <section class="profile-page__content">
          <div class="profile-page__content-header">
            <h2>{{ activeTabLabel }}观看历史</h2>
            <span>共 {{ total }} 条</span>
          </div>

          <div v-if="loading" class="profile-page__state">加载中...</div>
          <div v-else-if="!historyList.length" class="profile-page__state">暂无观看记录</div>
          <ul v-else class="profile-page__history">
            <li v-for="item in historyList" :key="item.id" class="profile-page__history-item">
              <router-link :to="item.path" class="profile-page__history-link">
                <div class="profile-page__history-cover">
                  <img v-if="item.coverImage" :src="item.coverImage" :alt="item.title" />
                  <div v-else class="profile-page__history-cover-placeholder">
                    <i class="el-icon-document" />
                  </div>
                </div>
                <div class="profile-page__history-info">
                  <h3>{{ item.title }}</h3>
                  <p>最近浏览：{{ item.createTime || '-' }}</p>
                </div>
              </router-link>
            </li>
          </ul>

          <div v-if="total > pageSize" class="profile-page__pagination">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page.sync="page"
              :page-size="pageSize"
              :total="total"
              @current-change="loadHistory"
            />
          </div>
        </section>
      </div>
    </main>

    <AppFooter />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import {
  fetchLogHistoryPage,
  LOG_HISTORY_TABS,
  mapLogHistoryItem
} from '@/api/profile'

export default {
  name: 'ProfilePage',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      historyTabs: LOG_HISTORY_TABS,
      activeType: LOG_HISTORY_TABS[0].value,
      historyList: [],
      loading: false,
      page: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    ...mapGetters(['userInfo', 'isLoggedIn']),
    activeTabLabel() {
      const tab = this.historyTabs.find((item) => item.value === this.activeType)
      return tab ? tab.label : ''
    }
  },
  watch: {
    isLoggedIn(loggedIn) {
      if (loggedIn) {
        this.loadHistory()
      } else {
        this.historyList = []
        this.total = 0
      }
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.loadHistory()
    }
  },
  methods: {
    switchTab(type) {
      if (this.activeType === type) return
      this.activeType = type
      this.page = 1
      this.loadHistory()
    },
    async loadHistory(page = this.page) {
      if (!this.isLoggedIn) return
      this.loading = true
      this.page = page
      try {
        const { records, total } = await fetchLogHistoryPage({
          type: this.activeType,
          page: this.page,
          limit: this.pageSize
        })
        this.historyList = (records || []).map(mapLogHistoryItem)
        this.total = total || 0
      } catch (error) {
        this.historyList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleLogout() {
      this.$store.dispatch('user/logout')
      this.$message.success('已退出登录')
      this.$router.push('/')
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-cream;

  &__main {
    flex: 1;
    width: 100%;
    max-width: $container-max-width;
    margin: 0 auto;
    min-height: calc(100vh - #{$header-height} - 220px);
    padding: calc(#{$header-height} + #{$spacing-2xl}) $content-padding $spacing-4xl;
  }

  &__guest {
    padding: $spacing-4xl;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    color: $color-text-light;
  }

  &__guest-link {
    display: inline-block;
    margin-top: $spacing-base;
    color: $success-color;
  }

  &__layout {
    display: flex;
    gap: $spacing-xl;
    align-items: stretch;
    min-height: 560px;
  }

  &__sidebar {
    width: 280px;
    flex-shrink: 0;
    padding: $spacing-xl;
    background: $color-white;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba($color-brown-dark, 0.08);
  }

  &__user-card {
    padding-bottom: $spacing-xl;
    border-bottom: 1px solid rgba($color-border, 0.35);
    text-align: center;
  }

  &__avatar {
    width: 72px;
    height: 72px;
    margin: 0 auto $spacing-base;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($success-color, 0.12);
    color: $success-color;
    font-size: rem(32);
  }

  &__name {
    margin: 0 0 $spacing-sm;
    font-family: $font-serif;
    font-size: rem(22);
    color: $color-brown-dark;
  }

  &__account,
  &__email {
    margin: 0 0 6px;
    font-size: rem(13);
    color: $color-text-light;
    word-break: break-all;
  }

  &__tabs {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
    padding: $spacing-xl 0;
  }

  &__tab {
    width: 100%;
    padding: 12px $spacing-base;
    border: 1px solid rgba($color-border, 0.45);
    border-radius: 4px;
    background: #fafafa;
    color: $color-text;
    font-size: rem(14);
    text-align: left;
    transition: all 0.2s;

    &--active,
    &:hover {
      color: $success-color;
      border-color: $success-color;
      background: rgba($success-color, 0.08);
    }
  }

  &__logout {
    width: 100%;
    padding: 12px $spacing-base;
    border: none;
    border-radius: 4px;
    background: $color-green-dark;
    color: $color-white;
    font-size: rem(14);
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    min-height: 560px;
    padding: $spacing-xl;
    background: $color-white;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba($color-brown-dark, 0.08);
    display: flex;
    flex-direction: column;
  }

  &__content-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-base;
    margin-bottom: $spacing-xl;
    padding-bottom: $spacing-base;
    border-bottom: 1px solid rgba($color-border, 0.35);

    h2 {
      margin: 0;
      font-family: $font-serif;
      font-size: rem(22);
      color: $color-brown-dark;
    }

    span {
      font-size: rem(13);
      color: $color-text-light;
    }
  }

  &__state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $spacing-4xl 0;
    text-align: center;
    color: $color-text-light;
    font-size: rem(14);
  }

  &__history {
    flex: 1;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__history-item {
    border-bottom: 1px solid rgba($color-border, 0.25);

    &:last-child {
      border-bottom: none;
    }
  }

  &__history-link {
    display: flex;
    gap: $spacing-base;
    padding: $spacing-base 0;
    color: inherit;
    transition: background 0.2s;

    &:hover {
      h3 {
        color: $success-color;
      }
    }
  }

  &__history-cover {
    width: 96px;
    height: 72px;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    background: $color-paper-light;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__history-cover-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-text-light;
    font-size: rem(24);
  }

  &__history-info {
    flex: 1;
    min-width: 0;

    h3 {
      margin: 0 0 8px;
      font-size: rem(16);
      font-weight: 600;
      color: $color-text;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.2s;
    }

    p {
      margin: 0;
      font-size: rem(13);
      color: $color-text-light;
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: $spacing-xl;
  }
}

@media (max-width: 960px) {
  .profile-page {
    &__layout {
      flex-direction: column;
    }

    &__sidebar {
      width: 100%;
    }

    &__tabs {
      flex-direction: row;
      flex-wrap: wrap;
    }

    &__tab {
      flex: 1;
      min-width: 88px;
      text-align: center;
    }
  }
}
</style>
